import Button from "@/components/Button"
import { CopyText } from "@/components/CopyText"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import {
  useSuspendPatientDeviceMutation,
  useUnlinkPatientDeviceMutation,
  useUnsuspendPatientDeviceMutation,
} from "@/data/queries"
import { Device, deviceTypeNameFor, deviceVendorNameFor } from "@/model/Devices"
import cn from "@/utils/cn"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { DateTimeFormatter, Instant, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

type DeviceListProps = {
  bloomlifeId: string
  timeZone: string
  devices: Device[]
}

type SuspendDeviceFormData = {
  reason: string
}

const suspendDeviceFormSchema = z.object({
  reason: z.string().min(1, { message: "Reason is required" }),
})

const DeviceList = ({
  bloomlifeId,
  timeZone,
  devices = [],
}: DeviceListProps) => {
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)

  const [showUnlinkConfirmationDialog, setShowUnlinkConfirmationDialog] =
    useState<boolean>(false)
  const [showSuspendConfirmationDialog, setShowSuspendConfirmationDialog] =
    useState<boolean>(false)
  const [showUnsuspendConfirmationDialog, setShowUnsuspendConfirmationDialog] =
    useState<boolean>(false)

  const { mutateAsync: unlinkDevice, isPending: isUnlinkingDevice } =
    useUnlinkPatientDeviceMutation(bloomlifeId)

  const { mutateAsync: suspendDevice, isPending: isSuspendingDevice } =
    useSuspendPatientDeviceMutation(bloomlifeId)

  const { mutateAsync: unsuspendDevice, isPending: isUnsuspendingDevice } =
    useUnsuspendPatientDeviceMutation(bloomlifeId)

  const { register, handleSubmit, formState, reset } =
    useForm<SuspendDeviceFormData>({
      mode: "onChange",
      resolver: zodResolver(suspendDeviceFormSchema),
      defaultValues: {
        reason: "",
      },
    })
  const { errors } = formState

  const openUnlinkDeviceModal = (device: Device) => {
    setSelectedDevice(device)
    setShowUnlinkConfirmationDialog(true)
  }
  const closeUnlinkDeviceModal = () => {
    setSelectedDevice(null)
    setShowUnlinkConfirmationDialog(false)
    reset()
  }
  const openSuspendDeviceModal = (device: Device) => {
    setSelectedDevice(device)
    setShowSuspendConfirmationDialog(true)
  }
  const closeSuspendDeviceModal = () => {
    setSelectedDevice(null)
    setShowSuspendConfirmationDialog(false)
    reset()
  }
  const openUnsuspendDeviceModal = (device: Device) => {
    setSelectedDevice(device)
    setShowUnsuspendConfirmationDialog(true)
  }
  const closeUnsuspendDeviceModal = () => {
    setSelectedDevice(null)
    setShowUnsuspendConfirmationDialog(false)
    reset()
  }

  const handleUnlinkDevice = async () => {
    try {
      console.log("Unlinking device:", selectedDevice)
      await unlinkDevice({
        vendor: selectedDevice!.vendor,
        serialNumber: selectedDevice!.serialNumber,
      })
      closeUnlinkDeviceModal()
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Device unlinked successfully." />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while unlinking this device. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const handleSuspendDevice = async (formData: SuspendDeviceFormData) => {
    try {
      console.log(
        "Suspending device:",
        selectedDevice,
        "Reason:",
        formData.reason
      )
      await suspendDevice({
        vendor: selectedDevice!.vendor,
        serialNumber: selectedDevice!.serialNumber,
        reason: formData.reason,
      })
      closeSuspendDeviceModal()
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Device suspended successfully." />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while suspending this device. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const handleUnsuspendDevice = async () => {
    try {
      console.log("unsuspending device:", selectedDevice)
      await unsuspendDevice({
        vendor: selectedDevice!.vendor,
        serialNumber: selectedDevice!.serialNumber,
      })
      closeUnsuspendDeviceModal()
      toast.custom((toastId) => (
        <Toast
          toastId={toastId}
          description="Device reactivated successfully."
        />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while reactivating this device. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <div>
      {!devices.length ? (
        <p className="italic text-primary/70">
          No device linked to this patient.
        </p>
      ) : (
        <div>
          <div className="grid grid-cols-7 gap-4">
            <InputLabel className="font-bold">Type</InputLabel>
            <InputLabel className="font-bold">Serial number</InputLabel>
            <InputLabel className="font-bold">IMEI</InputLabel>
            <InputLabel className="font-bold">Measurements</InputLabel>
            <InputLabel className="font-bold">Suspended at</InputLabel>
            <InputLabel className="font-bold">Suspend reason</InputLabel>
          </div>
          {devices.map((device) => (
            <div
              className="mt-3 grid grid-cols-7 items-center gap-2"
              key={device.id}
            >
              <p>
                {deviceVendorNameFor(device.vendor) +
                  " - " +
                  deviceTypeNameFor(device.type)}
              </p>
              <div className="break-all">
                <CopyText text={device.serialNumber} label="Serial number">
                  {device.serialNumber}
                </CopyText>
              </div>
              <div className="break-all">
                <CopyText text={device.imei ?? "-"} label="IMEI">
                  {device.imei ?? "-"}
                </CopyText>
              </div>
              <p className="">{device.numberOfReadings}</p>
              <p className="whitespace-pre">
                {device.suspendedAt
                  ? Instant.parse(device.suspendedAt.toString())
                      .atZone(ZoneId.of(timeZone))
                      .format(
                        DateTimeFormatter.ofPattern(
                          "MM/dd/yyyy\nhh:mm a"
                        ).withLocale(Locale.US)
                      )
                  : "-"}
              </p>
              <p className="whitespace-pre">{device.suspendReason ?? "-"}</p>
              <Popover className="relative justify-self-end">
                <PopoverButton className="flex h-[38px] w-[38px] items-center justify-center rounded-md border border-gray-300 bg-white focus-visible:outline-none">
                  <span
                    className={cn(
                      "material-icons cursor-pointer text-primary/60 transition-colors hover:text-primary",
                      isUnlinkingDevice ||
                        isSuspendingDevice ||
                        isUnsuspendingDevice
                        ? "pointer-events-none cursor-not-allowed text-primary/30"
                        : ""
                    )}
                  >
                    more_vert
                  </span>
                </PopoverButton>
                <PopoverPanel
                  anchor="bottom end"
                  className="flex w-56 flex-col rounded-lg border border-primary/20 bg-white shadow-lg"
                >
                  {!device.suspendedAt && (
                    <div
                      className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[16px] transition hover:bg-primary/5"
                      onClick={() => openSuspendDeviceModal(device)}
                    >
                      <span className="text-red-600">Suspend</span>
                    </div>
                  )}
                  {device.suspendedAt && (
                    <div
                      className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[16px] transition hover:bg-primary/5"
                      onClick={() => openUnsuspendDeviceModal(device)}
                    >
                      <span>Reactivate</span>
                    </div>
                  )}
                  <div
                    className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[16px] transition hover:bg-primary/5"
                    onClick={() => openUnlinkDeviceModal(device)}
                  >
                    <span className="text-red-600">Unlink</span>
                  </div>
                </PopoverPanel>
              </Popover>
            </div>
          ))}
        </div>
      )}

      <Dialog
        title="Unlink device"
        isOpen={!!selectedDevice && showUnlinkConfirmationDialog}
        className="max-w-md"
        onClose={closeUnlinkDeviceModal}
      >
        <form className="flex flex-col gap-4" autoComplete="off">
          <p>
            Are you sure you want to unlink this device from the selected
            patient?
          </p>
          <div className="mt-4 flex items-center justify-end gap-4">
            <Button
              type="button"
              onClick={handleUnlinkDevice}
              variant="primary"
              disabled={isUnlinkingDevice}
              className="flex items-center gap-3 px-6"
            >
              {isUnlinkingDevice && <Spinner className="size-4" />}
              Unlink
            </Button>
            <Button
              type="button"
              variant="neutral"
              className="px-6"
              onClick={closeUnlinkDeviceModal}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        title="Suspend device"
        isOpen={!!selectedDevice && showSuspendConfirmationDialog}
        className="max-w-md"
        onClose={closeSuspendDeviceModal}
      >
        <form
          onSubmit={handleSubmit(handleSuspendDevice)}
          className="flex flex-col gap-4"
          autoComplete="off"
        >
          <p>
            Are you sure you want to suspend this device from the selected
            patient?
          </p>
          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="reason">Reason</InputLabel>
            <TextInput
              id="reason"
              placeholder="Enter reason for suspending device"
              isError={!!errors?.reason?.message}
              {...register("reason")}
            />
            <InputErrorMessage
              message={errors?.reason?.message}
              className="ml-1"
            />
          </div>
          <div className="mt-4 flex items-center justify-end gap-4">
            <Button
              type="submit"
              variant="danger"
              disabled={isSuspendingDevice}
              className="flex items-center gap-3 px-6"
            >
              {isSuspendingDevice && <Spinner className="size-4" />}
              Suspend
            </Button>
            <Button
              type="button"
              variant="neutral"
              className="px-6"
              disabled={isSuspendingDevice}
              onClick={closeSuspendDeviceModal}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        title="Unsuspend device"
        isOpen={!!selectedDevice && showUnsuspendConfirmationDialog}
        className="max-w-md"
        onClose={closeUnsuspendDeviceModal}
      >
        <form className="flex flex-col gap-4" autoComplete="off">
          <p>
            Are you sure you want to reactivate this device for the selected
            patient?
          </p>
          <div className="mt-4 flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="neutral"
              className="px-6"
              disabled={isUnsuspendingDevice}
              onClick={closeUnsuspendDeviceModal}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUnsuspendDevice}
              variant="primary"
              disabled={isUnsuspendingDevice}
              className="flex items-center gap-3 px-6"
            >
              {isUnsuspendingDevice && <Spinner className="size-4" />}
              Reactivate
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  )
}

export default DeviceList
